/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import AppLoader from "components/AppLoader";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAdminOrderNotifications from "hooks/useAdminOrderNotifications";
import { formatDateTimeForUser, parseApiDateTime } from "utils/dateTime";

function Dashboard() {
  const latestOrdersLimit = 10;
  const dashboardCardHeight = "280px";
  const dashboardCardSx = {
    height: dashboardCardHeight,
    display: "flex",
    flexDirection: "column",
    border: "1px solid",
    borderColor: "rgba(76, 175, 80, 0.2)",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
    },
  };
  const [refreshTokenCount, setRefreshTokenCount] = useState(0);
  const [errorLogFileCount, setErrorLogFileCount] = useState(0);
  const [placedOrderCount, setPlacedOrderCount] = useState(0);
  const [paymentCompletedCount, setPaymentCompletedCount] = useState(0);
  const [cashOnDeliveryCount, setCashOnDeliveryCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const [isDeliveredOrdersLoading, setIsDeliveredOrdersLoading] = useState(false);
  const [paymentCompletedOrders, setPaymentCompletedOrders] = useState([]);
  const [isPaymentCompletedOrdersLoading, setIsPaymentCompletedOrdersLoading] = useState(false);
  const [cashOnDeliveryOrders, setCashOnDeliveryOrders] = useState([]);
  const [isCashOnDeliveryOrdersLoading, setIsCashOnDeliveryOrdersLoading] = useState(false);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [isCancelledOrdersLoading, setIsCancelledOrdersLoading] = useState(false);
  const [databaseSizeMb, setDatabaseSizeMb] = useState(0);
  const [activeTab, setActiveTab] = useState("orders");
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [invoiceSearchValue, setInvoiceSearchValue] = useState("");
  const [filteredOrders, setFilteredOrders] = useState(null);
  const [invoiceSearchMessage, setInvoiceSearchMessage] = useState("");
  const [isSearchingInvoice, setIsSearchingInvoice] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [statusSelections, setStatusSelections] = useState({});
  const [statusUpdatingOrderIds, setStatusUpdatingOrderIds] = useState({});
  const [orderStatusHistory, setOrderStatusHistory] = useState({});
  const [notificationSnackbar, setNotificationSnackbar] = useState({
    open: false,
    title: "New order received",
    content: "",
  });
  const [userRoleSummary, setUserRoleSummary] = useState({
    totalUsers: 0,
    membersWithoutRoles: 0,
    roleWiseCounts: [],
  });
  const activeTabRef = useRef(activeTab);
  const invoiceSearchValueRef = useRef(invoiceSearchValue);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    invoiceSearchValueRef.current = invoiceSearchValue;
  }, [invoiceSearchValue]);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const dedupeOrders = useCallback((items) => {
    if (!Array.isArray(items)) return [];

    const seen = new Set();
    return items.filter((item) => {
      const key = String(
        firstDefined(
          item?.orderId,
          item?.OrderId,
          item?.id,
          item?.Id,
          item?.referenceNo,
          item?.ReferenceNo
        ) ?? ""
      );

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, []);

  const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

  const readStatus = (item) =>
    String(
      firstDefined(
        item?.status,
        item?.Status,
        item?.orderStatus,
        item?.OrderStatus,
        item?.state,
        item?.State
      ) ?? ""
    )
      .trim()
      .toLowerCase();

  const getStatus = (item) =>
    String(
      firstDefined(
        item?.status,
        item?.Status,
        item?.orderStatus,
        item?.OrderStatus,
        item?.state,
        item?.State
      ) ?? "unknown"
    ).trim();

  const getStatusColor = (item) => {
    const rawStatus = readStatus(item);
    const status = rawStatus.replace(/[^a-z0-9]+/g, " ").trim();

    if (
      status.includes("payment completed") ||
      (status.includes("payment") && status.includes("completed")) ||
      status.includes("delivered")
    ) {
      return "success";
    }
    if (status.includes("cash on delivery")) {
      return "warning";
    }
    if (status.includes("cancel")) {
      return "secondary";
    }
    if (status.includes("placed")) {
      return "error";
    }
    if (
      status.includes("pending") ||
      status.includes("awaiting") ||
      status.includes("processing")
    ) {
      return "info";
    }
    return "secondary";
  };

  const isPaymentCompletedStatus = (item) => {
    const status = readStatus(item)
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
    return (
      status.includes("payment completed") ||
      (status.includes("payment") && status.includes("completed"))
    );
  };

  const getOrderId = (item) =>
    String(
      firstDefined(
        item?.orderId,
        item?.OrderId,
        item?.id,
        item?.Id,
        item?.referenceNo,
        item?.ReferenceNo
      ) ?? "-"
    );

  const getCustomer = (item) =>
    String(
      firstDefined(
        item?.customerName,
        item?.CustomerName,
        item?.customer,
        item?.Customer,
        item?.name,
        item?.Name
      ) ?? "-"
    );

  const formatAmount = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "-";
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getItemTotal = (item) =>
    formatAmount(firstDefined(item?.itemTotal, item?.ItemTotal, item?.amount, item?.Amount, 0));

  const getDeliveryFee = (item) =>
    formatAmount(firstDefined(item?.deliveryFee, item?.DeliveryFee, 0));

  const getGrandTotal = (item) =>
    formatAmount(
      firstDefined(
        item?.grandTotal,
        item?.GrandTotal,
        item?.totalAmount,
        item?.TotalAmount,
        item?.amount,
        item?.Amount,
        0
      )
    );

  const getOrderDateValue = (item) => {
    const value = firstDefined(
      item?.orderDate,
      item?.OrderDate,
      item?.createdDate,
      item?.CreatedDate,
      item?.createdOn,
      item?.CreatedOn
    );

    if (!value) return null;
    return parseApiDateTime(value);
  };

  const getCreatedDate = (item) => {
    return formatDateTimeForUser(getOrderDateValue(item));
  };

  const getCustomerEmail = (item) =>
    String(
      firstDefined(
        item?.email,
        item?.Email,
        item?.customerEmail,
        item?.CustomerEmail,
        item?.customer?.email,
        item?.Customer?.Email
      ) ?? "-"
    );

  const getCustomerPhone = (item) =>
    String(
      firstDefined(
        item?.phoneNumber,
        item?.PhoneNumber,
        item?.phoneNo,
        item?.PhoneNo,
        item?.customerPhone,
        item?.CustomerPhone,
        item?.customer?.phoneNumber,
        item?.Customer?.PhoneNumber
      ) ?? "-"
    );

  const getBillingAddress = (item) =>
    String(
      firstDefined(
        item?.billingAddress,
        item?.BillingAddress,
        item?.address,
        item?.Address,
        item?.customer?.billingAddress,
        item?.Customer?.BillingAddress
      ) ?? "-"
    );

  const getInvoiceNumber = (item) =>
    String(
      firstDefined(
        item?.orderId,
        item?.OrderId,
        item?.id,
        item?.Id,
        item?.invoiceNumber,
        item?.InvoiceNumber,
        item?.invoiceNo,
        item?.InvoiceNo,
        item?.invoiceId,
        item?.InvoiceId,
        item?.invoice?.number,
        item?.Invoice?.Number
      ) ?? "-"
    );

  const getInvoiceUrl = (item) => {
    const rawUrl = String(firstDefined(item?.invoiceUrl, item?.InvoiceUrl, "") ?? "").trim();
    if (rawUrl) {
      if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
      return `${appSettings.baseUrl}${rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`}`;
    }

    const invoiceNumber = getInvoiceNumber(item);
    if (!invoiceNumber || invoiceNumber === "-") return "";
    return `${appSettings.baseUrl}/invoices/${encodeURIComponent(invoiceNumber)}`;
  };

  const normalizeOrderStatus = (status) => {
    const normalized = String(status ?? "")
      .trim()
      .toLowerCase();
    if (normalized === "placed") return "Placed";
    if (normalized === "payment completed") return "Payment Completed";
    if (normalized === "cash on delivery") return "Cash on Delivery";
    if (normalized === "cancelled") return "Cancelled";
    if (normalized === "delivered") return "Delivered";
    return "Placed";
  };

  const getNextStatusOptions = (status) => {
    const current = normalizeOrderStatus(status);
    if (current === "Placed") return ["Payment Completed", "Cash on Delivery", "Cancelled"];
    if (current === "Payment Completed") return ["Delivered", "Cancelled"];
    if (current === "Cash on Delivery") return ["Delivered", "Cancelled"];
    return [];
  };

  const buildInferredStatusHistory = (status) => {
    const current = normalizeOrderStatus(status);
    if (current === "Placed") return ["Placed"];
    if (current === "Payment Completed") return ["Placed", "Payment Completed"];
    if (current === "Cash on Delivery") return ["Placed", "Cash on Delivery"];
    if (current === "Cancelled") return ["Placed", "Cancelled"];
    if (current === "Delivered") return ["Placed", "Payment Completed", "Delivered"];
    return ["Placed"];
  };

  const getOrderStatusHistory = (order) => {
    const orderId = Number(getOrderId(order));
    const fromSession = orderStatusHistory[orderId];
    if (Array.isArray(fromSession) && fromSession.length > 0) {
      return fromSession;
    }
    return buildInferredStatusHistory(getStatus(order));
  };

  const fetchPendingOrders = async ({
    limit = latestOrdersLimit,
    invoiceNo = "",
    status = "",
  } = {}) => {
    const endpoint = appSettings?.endpoints?.getPendingOrders;
    if (!endpoint) return [];

    const url = `${appSettings.baseUrl}${endpoint}?limit=${limit}&invoiceNo=${encodeURIComponent(
      invoiceNo
    )}&status=${encodeURIComponent(status)}&_ts=${Date.now()}`;
    const response = await fetchWithAuth(url, {
      method: "GET",
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch orders.");
    }

    const data = await response.json();
    return normalizeArray(
      firstDefined(data?.orders, data?.Orders, data?.pendingOrders, data?.PendingOrders, data)
    );
  };

  const openInvoicePdf = async (item) => {
    const url = getInvoiceUrl(item);
    if (!url) return;
    try {
      const response = await fetchWithAuth(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to load invoice.");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const popup = window.open(objectUrl, "_blank", "noopener,noreferrer");

      if (!popup) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = "invoice.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      setInvoiceSearchMessage(error?.message || "Failed to open invoice.");
    }
  };

  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsOrderDetailOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setIsOrderDetailOpen(false);
    setSelectedOrder(null);
  };

  const handleSearchInvoice = async () => {
    const keyword = invoiceSearchValue.trim();
    if (!keyword) {
      setFilteredOrders(null);
      setInvoiceSearchMessage("");
      return;
    }

    const normalizeInvoice = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();
    const matchesKeyword = (item) =>
      normalizeInvoice(getInvoiceNumber(item)).includes(normalizeInvoice(keyword));

    setIsSearchingInvoice(true);
    setInvoiceSearchMessage("Searching in database...");

    try {
      const fetched = await fetchPendingOrders({
        limit: latestOrdersLimit,
        invoiceNo: keyword,
        status: "placed",
      });
      const matchedFromDb = fetched.filter(matchesKeyword);

      if (matchedFromDb.length > 0) {
        setFilteredOrders(matchedFromDb);
        setInvoiceSearchMessage(`Found ${matchedFromDb.length} record(s) from database.`);
      } else {
        setFilteredOrders([]);
        setInvoiceSearchMessage("No records found for this invoice number.");
      }
    } catch (e) {
      setFilteredOrders([]);
      setInvoiceSearchMessage(e.message || "Invoice search failed.");
    } finally {
      setIsSearchingInvoice(false);
    }
  };

  const clearInvoiceSearch = () => {
    setInvoiceSearchValue("");
    setFilteredOrders(null);
    setInvoiceSearchMessage("");
  };

  const handleDownloadOrdersReport = async (statusFilter = "", fileLabel = "orders-report") => {
    const endpoint = appSettings?.endpoints?.downloadOrdersReport;
    if (!endpoint) {
      setInvoiceSearchMessage("Orders report endpoint is not configured.");
      return;
    }

    setIsGeneratingReport(true);
    try {
      const url = `${appSettings.baseUrl}${endpoint}?status=${encodeURIComponent(
        statusFilter
      )}&_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to download orders report.");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${fileLabel}-${new Date().toISOString().replace(/[:.]/g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setInvoiceSearchMessage(error?.message || "Failed to download orders report.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const refreshOrdersData = useCallback(async () => {
    const allOrders = dedupeOrders(await fetchPendingOrders({ limit: 0, status: "placed" }));
    const placedCount = allOrders.length;
    setPlacedOrderCount(placedCount);
    const paymentCompleted = dedupeOrders(
      await fetchPendingOrders({ limit: 0, status: "payment completed" })
    );
    setPaymentCompletedCount(paymentCompleted.length);
    const cashOnDelivery = dedupeOrders(
      await fetchPendingOrders({ limit: 0, status: "cash on delivery" })
    );
    setCashOnDeliveryCount(cashOnDelivery.length);
    const cancelled = dedupeOrders(await fetchPendingOrders({ limit: 0, status: "cancelled" }));
    setCancelledCount(cancelled.length);

    if (invoiceSearchValueRef.current.trim()) {
      const invoiceFiltered = dedupeOrders(
        await fetchPendingOrders({
          limit: latestOrdersLimit,
          invoiceNo: invoiceSearchValueRef.current.trim(),
          status: "placed",
        })
      );
      setFilteredOrders(invoiceFiltered);
    } else {
      const topOrders = allOrders.slice(0, latestOrdersLimit);
      setOrders(topOrders);
      setFilteredOrders(null);
    }

    if (activeTabRef.current === "delivered") {
      const delivered = dedupeOrders(
        await fetchPendingOrders({ limit: latestOrdersLimit, status: "delivered" })
      );
      setDeliveredOrders(delivered);
    }
    if (activeTabRef.current === "paymentCompleted") {
      const list = dedupeOrders(
        await fetchPendingOrders({
          limit: latestOrdersLimit,
          status: "payment completed",
        })
      );
      setPaymentCompletedOrders(list);
    }
    if (activeTabRef.current === "cashOnDelivery") {
      const list = dedupeOrders(
        await fetchPendingOrders({
          limit: latestOrdersLimit,
          status: "cash on delivery",
        })
      );
      setCashOnDeliveryOrders(list);
    }
    if (activeTabRef.current === "cancelled") {
      const list = dedupeOrders(
        await fetchPendingOrders({ limit: latestOrdersLimit, status: "cancelled" })
      );
      setCancelledOrders(list);
    }
  }, [dedupeOrders]);

  const loadDashboardData = useCallback(
    async ({ showLoading = false } = {}) => {
      const fetchRefreshTokensSummary = async () => {
        const endpoint = appSettings?.endpoints?.getAllRefreshTokens;
        if (!endpoint) return;

        const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
        try {
          const response = await fetchWithAuth(url, {
            method: "GET",
            cache: "no-store",
          });

          if (!response.ok) return;

          const data = await response.json();
          const list = Array.isArray(data) ? data : data?.$values;
          setRefreshTokenCount(Array.isArray(list) ? list.length : 0);
        } catch (e) {
          setRefreshTokenCount(0);
        }
      };

      const fetchErrorLogFilesSummary = async () => {
        const endpoint = appSettings?.endpoints?.getErrorLogFiles;
        if (!endpoint) return;

        const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
        try {
          const response = await fetchWithAuth(url, {
            method: "GET",
            cache: "no-store",
          });

          if (!response.ok) return;

          const data = await response.json();
          const list = Array.isArray(data) ? data : data?.$values;
          setErrorLogFileCount(Array.isArray(list) ? list.length : 0);
        } catch (e) {
          setErrorLogFileCount(0);
        }
      };

      const fetchDatabaseSizeSummary = async () => {
        const endpoint = appSettings?.endpoints?.getDatabaseSizeMb;
        if (!endpoint) return;

        const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
        try {
          const response = await fetchWithAuth(url, {
            method: "GET",
            cache: "no-store",
          });

          if (!response.ok) return;

          const data = await response.json();
          const parsedValue = Number(data?.sizeInMb);
          setDatabaseSizeMb(Number.isFinite(parsedValue) ? parsedValue : 0);
        } catch (e) {
          setDatabaseSizeMb(0);
        }
      };

      const fetchUserRoleCountSummary = async () => {
        const endpoint = appSettings?.endpoints?.getUserRoleCountSummary;
        if (!endpoint) return;

        const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
        try {
          const response = await fetchWithAuth(url, {
            method: "GET",
            cache: "no-store",
          });

          if (!response.ok) return;

          const data = await response.json();
          const totalUsers = Number(data?.totalUsers);
          const membersWithoutRoles = Number(data?.membersWithoutRoles);
          const rawRoleCounts = Array.isArray(data?.roleWiseCounts)
            ? data.roleWiseCounts
            : data?.roleWiseCounts?.$values;

          setUserRoleSummary({
            totalUsers: Number.isFinite(totalUsers) ? totalUsers : 0,
            membersWithoutRoles: Number.isFinite(membersWithoutRoles) ? membersWithoutRoles : 0,
            roleWiseCounts: Array.isArray(rawRoleCounts) ? rawRoleCounts : [],
          });
        } catch (e) {
          setUserRoleSummary({ totalUsers: 0, membersWithoutRoles: 0, roleWiseCounts: [] });
        }
      };

      if (showLoading) {
        setIsDashboardLoading(true);
      }

      try {
        await Promise.all([
          fetchRefreshTokensSummary(),
          fetchErrorLogFilesSummary(),
          refreshOrdersData(),
          fetchDatabaseSizeSummary(),
          fetchUserRoleCountSummary(),
        ]);
        console.info("Dashboard data refreshed");
      } finally {
        if (showLoading) {
          setIsDashboardLoading(false);
        }
      }
    },
    [refreshOrdersData]
  );

  const loadDeliveredOrders = async () => {
    setIsDeliveredOrdersLoading(true);
    try {
      const delivered = await fetchPendingOrders({ limit: latestOrdersLimit, status: "delivered" });
      setDeliveredOrders(delivered);
    } catch (error) {
      setDeliveredOrders([]);
      setInvoiceSearchMessage(error?.message || "Failed to fetch delivered orders.");
    } finally {
      setIsDeliveredOrdersLoading(false);
    }
  };

  const loadPaymentCompletedOrders = async () => {
    setIsPaymentCompletedOrdersLoading(true);
    try {
      const list = await fetchPendingOrders({
        limit: latestOrdersLimit,
        status: "payment completed",
      });
      setPaymentCompletedOrders(list);
    } catch (error) {
      setPaymentCompletedOrders([]);
      setInvoiceSearchMessage(error?.message || "Failed to fetch payment completed orders.");
    } finally {
      setIsPaymentCompletedOrdersLoading(false);
    }
  };

  const loadCashOnDeliveryOrders = async () => {
    setIsCashOnDeliveryOrdersLoading(true);
    try {
      const list = await fetchPendingOrders({
        limit: latestOrdersLimit,
        status: "cash on delivery",
      });
      setCashOnDeliveryOrders(list);
    } catch (error) {
      setCashOnDeliveryOrders([]);
      setInvoiceSearchMessage(error?.message || "Failed to fetch cash on delivery orders.");
    } finally {
      setIsCashOnDeliveryOrdersLoading(false);
    }
  };

  const loadCancelledOrders = async () => {
    setIsCancelledOrdersLoading(true);
    try {
      const list = await fetchPendingOrders({
        limit: latestOrdersLimit,
        status: "cancelled",
      });
      setCancelledOrders(list);
    } catch (error) {
      setCancelledOrders([]);
      setInvoiceSearchMessage(error?.message || "Failed to fetch cancelled orders.");
    } finally {
      setIsCancelledOrdersLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (item) => {
    const orderId = Number(getOrderId(item));
    const nextStatus = statusSelections[orderId];
    if (!orderId || !nextStatus) return;

    const endpoint = appSettings?.endpoints?.updateOrderStatus;
    if (!endpoint) {
      setInvoiceSearchMessage("Order status update endpoint is not configured.");
      return;
    }

    setStatusUpdatingOrderIds((prev) => ({ ...prev, [orderId]: true }));
    try {
      const response = await fetchWithAuth(`${appSettings.baseUrl}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, status: nextStatus }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(
            "Update status API route not found (404). Please restart Expenditure.Api and Ocelot.Api.Gateway."
          );
        }
        throw new Error(payload?.message || "Failed to update order status.");
      }

      const updatedStatus = String(payload?.currentStatus || nextStatus).trim();
      const previousStatus = String(payload?.previousStatus || "").trim();
      const applyStatus = (list) =>
        Array.isArray(list)
          ? list.map((order) => {
              const currentOrderId = Number(getOrderId(order));
              if (currentOrderId !== orderId) return order;
              return {
                ...order,
                status: updatedStatus,
                Status: updatedStatus,
              };
            })
          : list;

      setOrders((prev) => applyStatus(prev));
      setFilteredOrders((prev) => applyStatus(prev));
      setOrderStatusHistory((prev) => {
        const existing = Array.isArray(prev[orderId]) ? [...prev[orderId]] : [];
        if (existing.length === 0 && previousStatus) {
          existing.push(normalizeOrderStatus(previousStatus));
        }
        existing.push(normalizeOrderStatus(updatedStatus));
        const deduped = existing.filter(
          (value, index) => index === 0 || value !== existing[index - 1]
        );
        return { ...prev, [orderId]: deduped };
      });

      setInvoiceSearchMessage(
        `Order #${orderId} moved to "${updatedStatus}" and customer notified.`
      );
      setStatusSelections((prev) => ({ ...prev, [orderId]: "" }));
      await refreshOrdersData();
    } catch (error) {
      setInvoiceSearchMessage(error?.message || "Failed to update order status.");
    } finally {
      setStatusUpdatingOrderIds((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  useEffect(() => {
    loadDashboardData({ showLoading: true });
  }, [loadDashboardData]);

  const handleIncomingNotification = useCallback(
    async (notification) => {
      const alertMessage = String(
        notification?.message || notification?.title || "New order received."
      );
      setNotificationSnackbar({
        open: true,
        title: "Order Notification",
        content: alertMessage,
      });
      await loadDashboardData();
    },
    [loadDashboardData]
  );

  useAdminOrderNotifications({
    onNotification: handleIncomingNotification,
  });

  useEffect(() => {
    if (activeTab !== "delivered") return;
    loadDeliveredOrders();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "paymentCompleted") return;
    loadPaymentCompletedOrders();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "cashOnDelivery") return;
    loadCashOnDeliveryOrders();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "cancelled") return;
    loadCancelledOrders();
  }, [activeTab]);

  const defaultGaugeOptions = {
    gaugeMax: 200,
    dialMarks: [0, 50, 100, 150, 200],
    dialRanges: [
      { from: 0, to: 50, color: "#ffffff" },
      { from: 50, to: 100, color: "#fdd835" },
      { from: 100, to: 150, color: "#fb8c00" },
      { from: 150, to: 200, color: "#e53935" },
    ],
    formatValue: (value) => value,
  };

  const dbSizeGaugeOptions = {
    gaugeMax: 500,
    dialMarks: [0, 100, 200, 300, 500],
    dialRanges: [
      { from: 0, to: 100, color: "#43a047" },
      { from: 100, to: 200, color: "#fdd835" },
      { from: 200, to: 300, color: "#fb8c00" },
      { from: 300, to: 500, color: "#e53935" },
    ],
    formatValue: (value) => `${Math.round(value * 100) / 100} MB`,
  };

  const polarToCartesian = (cx, cy, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    };
  };

  const renderGaugeCard = (title, value, icon, iconColor, options = defaultGaugeOptions) => {
    const gaugeStartAngle = 180;
    const gaugeEndAngle = 360;
    const gaugeMax = options.gaugeMax ?? defaultGaugeOptions.gaugeMax;
    const dialMarks = options.dialMarks ?? defaultGaugeOptions.dialMarks;
    const dialRanges = options.dialRanges ?? defaultGaugeOptions.dialRanges;
    const formatValue = options.formatValue ?? defaultGaugeOptions.formatValue;

    const valueToAngle = (pointValue) =>
      gaugeStartAngle + (pointValue / gaugeMax) * (gaugeEndAngle - gaugeStartAngle);

    const getNeedleAngle = (pointValue) => {
      const clamped = Math.min(Math.max(pointValue, 0), gaugeMax);
      return gaugeStartAngle + (clamped / gaugeMax) * (gaugeEndAngle - gaugeStartAngle);
    };

    const createArcPath = (cx, cy, radius, startValue, endValue) => {
      const startAngle = valueToAngle(startValue);
      const endAngle = valueToAngle(endValue);
      const start = polarToCartesian(cx, cy, radius, startAngle);
      const end = polarToCartesian(cx, cy, radius, endAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
      return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
    };

    const needleAngle = getNeedleAngle(value);

    return (
      <Card sx={dashboardCardSx}>
        <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
          <MDBox
            variant="gradient"
            bgColor={iconColor}
            color="white"
            coloredShadow={iconColor}
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <Icon fontSize="medium" color="inherit">
              {icon}
            </Icon>
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography
              variant="caption"
              fontWeight="regular"
              color="text"
              sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
            >
              {title}
            </MDTypography>
            <MDTypography variant="h4" color="text">
              {formatValue(value)}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={2} pt={1} sx={{ flex: 1 }}>
          <MDBox mt={2} mx="auto" width="100%" maxWidth="220px">
            <svg viewBox="0 0 220 150" width="100%" height="auto">
              {dialRanges.map((range) => (
                <path
                  key={`${title}-range-${range.from}-${range.to}`}
                  d={createArcPath(110, 105, 70, range.from, range.to)}
                  stroke={range.color}
                  strokeWidth="16"
                  fill="none"
                  strokeLinecap="butt"
                />
              ))}

              {Array.from({ length: 21 }).map((_, index) => {
                const tickValue = (gaugeMax / 20) * index;
                const angle = valueToAngle(tickValue);
                const outer = polarToCartesian(110, 105, 84, angle);
                const inner = polarToCartesian(110, 105, 76, angle);
                return (
                  <line
                    key={`${title}-tick-${index}`}
                    x1={outer.x}
                    y1={outer.y}
                    x2={inner.x}
                    y2={inner.y}
                    stroke="#9e9e9e"
                    strokeWidth="1.5"
                  />
                );
              })}

              {dialMarks.map((mark) => {
                const angle = valueToAngle(mark);
                const position = polarToCartesian(110, 105, 98, angle);
                return (
                  <text
                    key={`${title}-label-${mark}`}
                    x={position.x}
                    y={position.y}
                    fill="#757575"
                    fontSize="13"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {mark}
                  </text>
                );
              })}

              <line
                x1="110"
                y1="105"
                x2={polarToCartesian(110, 105, 58, needleAngle).x}
                y2={polarToCartesian(110, 105, 58, needleAngle).y}
                stroke="#757575"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx="110" cy="105" r="5" fill="#757575" />
            </svg>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  const renderUserRoleSummaryCard = () => (
    <Card sx={dashboardCardSx}>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor="success"
          color="white"
          coloredShadow="success"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            groups
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="caption"
            fontWeight="regular"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Total Users
          </MDTypography>
          <MDTypography variant="h4" color="text">
            {userRoleSummary.totalUsers}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox p={2} pt={1} sx={{ flex: 1, minHeight: 0 }}>
        <MDBox display="flex" justifyContent="space-between" py={0.5}>
          <MDTypography variant="caption" color="text">
            Members Without Roles
          </MDTypography>
          <MDTypography variant="caption" fontWeight="medium" color="text">
            {userRoleSummary.membersWithoutRoles}
          </MDTypography>
        </MDBox>
        <MDTypography variant="button" fontWeight="light" color="text">
          Role Wise User Counts
        </MDTypography>
        <MDBox
          mt={1}
          sx={{
            maxHeight: "140px",
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {userRoleSummary.roleWiseCounts.length > 0 ? (
            userRoleSummary.roleWiseCounts.map((item) => (
              <MDBox
                key={`role-count-${item.roleId ?? item.RoleId ?? item.roleName ?? item.RoleName}`}
                display="flex"
                justifyContent="space-between"
                py={0.5}
              >
                <MDTypography variant="caption" color="text">
                  {item.roleName ?? item.RoleName ?? "-"}
                </MDTypography>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  {item.userCount ?? item.UserCount ?? 0}
                </MDTypography>
              </MDBox>
            ))
          ) : (
            <MDTypography variant="caption" color="text">
              No roles available.
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );

  const renderPlacedOrdersCard = () => (
    <Card
      sx={{
        ...dashboardCardSx,
        height: "180px",
      }}
    >
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor="info"
          color="white"
          coloredShadow="info"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            shopping_cart
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="caption"
            fontWeight="regular"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Placed Orders
          </MDTypography>
          <MDTypography variant="h3" color="text">
            {placedOrderCount}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2} pt={1}>
        <MDTypography variant="button" color="text">
          Total count of placed orders
        </MDTypography>
      </MDBox>
    </Card>
  );

  const renderPaymentCompletedCard = () => (
    <Card
      sx={{
        ...dashboardCardSx,
        height: "180px",
      }}
    >
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor="success"
          color="white"
          coloredShadow="success"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            task_alt
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="caption"
            fontWeight="regular"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Payment Completed
          </MDTypography>
          <MDTypography variant="h3" color="text">
            {paymentCompletedCount}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2} pt={1}>
        <MDTypography variant="button" color="text">
          Total count of payment completed orders
        </MDTypography>
      </MDBox>
    </Card>
  );

  const renderCashOnDeliveryCard = () => (
    <Card
      sx={{
        ...dashboardCardSx,
        height: "180px",
      }}
    >
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor="warning"
          color="white"
          coloredShadow="warning"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            local_shipping
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="caption"
            fontWeight="regular"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Cash on Delivery
          </MDTypography>
          <MDTypography variant="h3" color="text">
            {cashOnDeliveryCount}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2} pt={1}>
        <MDTypography variant="button" color="text">
          Total count of cash on delivery orders
        </MDTypography>
      </MDBox>
    </Card>
  );

  const renderCancelledCard = () => (
    <Card
      sx={{
        ...dashboardCardSx,
        height: "180px",
      }}
    >
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor="secondary"
          color="white"
          coloredShadow="secondary"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            cancel
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="caption"
            fontWeight="regular"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Cancelled
          </MDTypography>
          <MDTypography variant="h3" color="text">
            {cancelledCount}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2} pt={1}>
        <MDTypography variant="button" color="text">
          Total count of cancelled orders
        </MDTypography>
      </MDBox>
    </Card>
  );

  const buildOrderRows = (sourceOrders, showStatusActions = true) =>
    sourceOrders.map((item) => ({
      invoiceNo: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {getInvoiceNumber(item)}
        </MDTypography>
      ),
      customer: (
        <MDTypography variant="caption" color="text">
          {getCustomer(item)}
        </MDTypography>
      ),
      itemTotal: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {getItemTotal(item)}
        </MDTypography>
      ),
      deliveryFee: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {getDeliveryFee(item)}
        </MDTypography>
      ),
      grandTotal: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {getGrandTotal(item)}
        </MDTypography>
      ),
      status: (
        <MDBadge
          badgeContent={getStatus(item)}
          color={getStatusColor(item)}
          variant="gradient"
          size="sm"
          sx={
            isPaymentCompletedStatus(item)
              ? {
                  background: "linear-gradient(195deg, #4caf50, #2e7d32) !important",
                  color: "#ffffff !important",
                }
              : undefined
          }
        />
      ),
      created: (
        <MDTypography variant="caption" color="text">
          {getCreatedDate(item)}
        </MDTypography>
      ),
      action: (
        <MDBox display="flex" gap={1} justifyContent="center">
          {showStatusActions && (
            <>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                  displayEmpty
                  value={statusSelections[Number(getOrderId(item))] || ""}
                  onChange={(event) =>
                    setStatusSelections((prev) => ({
                      ...prev,
                      [Number(getOrderId(item))]: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Change status</MenuItem>
                  {getNextStatusOptions(getStatus(item)).map((statusOption) => (
                    <MenuItem key={`${getOrderId(item)}-${statusOption}`} value={statusOption}>
                      {statusOption}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleUpdateOrderStatus(item)}
                disabled={
                  !statusSelections[Number(getOrderId(item))] ||
                  Boolean(statusUpdatingOrderIds[Number(getOrderId(item))])
                }
                sx={{
                  textTransform: "none",
                  color: "#ffffff !important",
                  fontWeight: 700,
                  "&.Mui-disabled": {
                    color: "#f1f5f9 !important",
                    bgcolor: "#94a3b8",
                  },
                }}
              >
                {statusUpdatingOrderIds[Number(getOrderId(item))] ? "Updating..." : "Update"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setStatusSelections((prev) => ({
                    ...prev,
                    [Number(getOrderId(item))]: "",
                  }))
                }
                disabled={
                  !statusSelections[Number(getOrderId(item))] ||
                  Boolean(statusUpdatingOrderIds[Number(getOrderId(item))])
                }
                sx={{
                  textTransform: "none",
                  color: "#b71c2a",
                  borderColor: "#fda4af",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "#fb7185",
                    bgcolor: "#fff1f2",
                  },
                  "&.Mui-disabled": {
                    color: "#94a3b8",
                    borderColor: "#cbd5e1",
                  },
                }}
              >
                Reset
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleViewOrderDetails(item)}
            sx={{
              textTransform: "none",
              color: "#d32f2f",
              borderColor: "#d32f2f",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#b71c1c",
                color: "#b71c1c",
              },
              "&.Mui-disabled": {
                color: "#d32f2f",
                borderColor: "#d32f2f",
                opacity: 0.7,
              },
            }}
          >
            View
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => openInvoicePdf(item)}
            disabled={!getInvoiceUrl(item)}
            sx={{
              textTransform: "none",
              color: "#fff",
              fontWeight: 700,
              "&.Mui-disabled": {
                color: "#fff",
                opacity: 0.65,
              },
            }}
          >
            Invoice
          </Button>
        </MDBox>
      ),
    }));

  const latestOrdersTable = useMemo(() => {
    const columns = [
      { Header: "invoice #", accessor: "invoiceNo", align: "left" },
      { Header: "customer", accessor: "customer", align: "left" },
      { Header: "item total", accessor: "itemTotal", align: "right" },
      { Header: "delivery fee", accessor: "deliveryFee", align: "right" },
      { Header: "grand total", accessor: "grandTotal", align: "right" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "created", accessor: "created", align: "left" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const sourceOrders = Array.isArray(filteredOrders) ? filteredOrders : orders;
    const rows = buildOrderRows(sourceOrders, true);

    return { columns, rows };
  }, [orders, filteredOrders, statusSelections, statusUpdatingOrderIds]);

  const deliveredOrdersTable = useMemo(() => {
    const columns = [
      { Header: "invoice #", accessor: "invoiceNo", align: "left" },
      { Header: "customer", accessor: "customer", align: "left" },
      { Header: "item total", accessor: "itemTotal", align: "right" },
      { Header: "delivery fee", accessor: "deliveryFee", align: "right" },
      { Header: "grand total", accessor: "grandTotal", align: "right" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "created", accessor: "created", align: "left" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const rows = buildOrderRows(deliveredOrders, false);
    return { columns, rows };
  }, [deliveredOrders]);

  const paymentCompletedOrdersTable = useMemo(() => {
    const columns = [
      { Header: "invoice #", accessor: "invoiceNo", align: "left" },
      { Header: "customer", accessor: "customer", align: "left" },
      { Header: "item total", accessor: "itemTotal", align: "right" },
      { Header: "delivery fee", accessor: "deliveryFee", align: "right" },
      { Header: "grand total", accessor: "grandTotal", align: "right" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "created", accessor: "created", align: "left" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const rows = buildOrderRows(paymentCompletedOrders, true);
    return { columns, rows };
  }, [paymentCompletedOrders, statusSelections, statusUpdatingOrderIds]);

  const cashOnDeliveryOrdersTable = useMemo(() => {
    const columns = [
      { Header: "invoice #", accessor: "invoiceNo", align: "left" },
      { Header: "customer", accessor: "customer", align: "left" },
      { Header: "item total", accessor: "itemTotal", align: "right" },
      { Header: "delivery fee", accessor: "deliveryFee", align: "right" },
      { Header: "grand total", accessor: "grandTotal", align: "right" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "created", accessor: "created", align: "left" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const rows = buildOrderRows(cashOnDeliveryOrders, true);
    return { columns, rows };
  }, [cashOnDeliveryOrders, statusSelections, statusUpdatingOrderIds]);

  const cancelledOrdersTable = useMemo(() => {
    const columns = [
      { Header: "invoice #", accessor: "invoiceNo", align: "left" },
      { Header: "customer", accessor: "customer", align: "left" },
      { Header: "item total", accessor: "itemTotal", align: "right" },
      { Header: "delivery fee", accessor: "deliveryFee", align: "right" },
      { Header: "grand total", accessor: "grandTotal", align: "right" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "created", accessor: "created", align: "left" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const rows = buildOrderRows(cancelledOrders, false);
    return { columns, rows };
  }, [cancelledOrders]);

  const isAnyStatusUpdateInProgress = Object.values(statusUpdatingOrderIds).some(Boolean);
  const isProcessLoading = isSearchingInvoice || isAnyStatusUpdateInProgress || isGeneratingReport;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isDashboardLoading ? (
        <MDBox minHeight="60vh" display="flex" alignItems="center" justifyContent="center">
          <AppLoader size={48} />
        </MDBox>
      ) : (
        <>
          <MDBox py={3}>
            {activeTab !== "overview" && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {renderPlacedOrdersCard()}
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {renderPaymentCompletedCard()}
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {renderCashOnDeliveryCard()}
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  {renderCancelledCard()}
                </Grid>
              </Grid>
            )}

            <Card sx={{ mb: 3 }}>
              <MDBox px={2} pt={1}>
                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
                  <Tab label="Orders" value="orders" />
                  <Tab label="Payment Completed" value="paymentCompleted" />
                  <Tab label="Cash on Delivery" value="cashOnDelivery" />
                  <Tab label="Cancelled" value="cancelled" />
                  <Tab label="Delivered" value="delivered" />
                  <Tab label="Overview" value="overview" />
                </Tabs>
              </MDBox>
            </Card>

            {activeTab === "orders" && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox px={2} py={2}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", md: "center" }}
                        flexDirection={{ xs: "column", md: "row" }}
                        gap={1.5}
                      >
                        <MDTypography variant="h6" color="text">
                          Latest 10 Orders
                        </MDTypography>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexDirection={{ xs: "column", sm: "row" }}
                        >
                          <Button
                            variant="outlined"
                            onClick={handleDownloadOrdersReport}
                            sx={{
                              textTransform: "none",
                              color: "#d32f2f",
                              borderColor: "#d32f2f",
                              "&:hover": {
                                borderColor: "#b71c1c",
                                color: "#b71c1c",
                              },
                              "&.Mui-disabled": {
                                color: "#d32f2f",
                                borderColor: "#d32f2f",
                                opacity: 0.6,
                              },
                            }}
                          >
                            Download PDF Report
                          </Button>
                          <MDInput
                            label="Search by Invoice No"
                            value={invoiceSearchValue}
                            onChange={(e) => setInvoiceSearchValue(e.target.value)}
                          />
                          <Button
                            variant="contained"
                            onClick={handleSearchInvoice}
                            disabled={isSearchingInvoice}
                            sx={{
                              textTransform: "none",
                              color: "#fff",
                              "&.Mui-disabled": {
                                color: "#fff",
                                opacity: 0.65,
                              },
                            }}
                          >
                            Search
                          </Button>
                          {filteredOrders !== null && (
                            <Button
                              variant="outlined"
                              onClick={clearInvoiceSearch}
                              sx={{ textTransform: "none" }}
                            >
                              Clear
                            </Button>
                          )}
                        </MDBox>
                      </MDBox>
                      {invoiceSearchMessage && (
                        <MDTypography variant="caption" color="text" display="block" mt={1}>
                          {invoiceSearchMessage}
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDBox pb={2}>
                      <DataTable
                        table={latestOrdersTable}
                        isSorted
                        canSearch={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                      />
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === "delivered" && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox
                      px={2}
                      py={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", md: "center" }}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1.5}
                    >
                      <MDTypography variant="h6" color="text">
                        Delivered Orders
                      </MDTypography>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleDownloadOrdersReport("delivered", "delivered-orders-report")
                        }
                        disabled={isGeneratingReport}
                        sx={{
                          textTransform: "none",
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                          "&:hover": {
                            borderColor: "#b71c1c",
                            color: "#b71c1c",
                          },
                          "&.Mui-disabled": {
                            color: "#d32f2f",
                            borderColor: "#d32f2f",
                            opacity: 0.6,
                          },
                        }}
                      >
                        Download PDF Report
                      </Button>
                    </MDBox>
                    <MDBox pb={2}>
                      {isDeliveredOrdersLoading ? (
                        <MDBox py={5} display="flex" justifyContent="center">
                          <AppLoader size={36} />
                        </MDBox>
                      ) : (
                        <DataTable
                          table={deliveredOrdersTable}
                          isSorted
                          canSearch={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === "paymentCompleted" && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox
                      px={2}
                      py={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", md: "center" }}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1.5}
                    >
                      <MDTypography variant="h6" color="text">
                        Payment Completed Orders
                      </MDTypography>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleDownloadOrdersReport(
                            "payment completed",
                            "payment-completed-orders-report"
                          )
                        }
                        disabled={isGeneratingReport}
                        sx={{
                          textTransform: "none",
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                          "&:hover": {
                            borderColor: "#b71c1c",
                            color: "#b71c1c",
                          },
                          "&.Mui-disabled": {
                            color: "#d32f2f",
                            borderColor: "#d32f2f",
                            opacity: 0.6,
                          },
                        }}
                      >
                        Download PDF Report
                      </Button>
                    </MDBox>
                    <MDBox pb={2}>
                      {isPaymentCompletedOrdersLoading ? (
                        <MDBox py={5} display="flex" justifyContent="center">
                          <AppLoader size={36} />
                        </MDBox>
                      ) : (
                        <DataTable
                          table={paymentCompletedOrdersTable}
                          isSorted
                          canSearch={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === "cashOnDelivery" && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox
                      px={2}
                      py={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", md: "center" }}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1.5}
                    >
                      <MDTypography variant="h6" color="text">
                        Cash on Delivery Orders
                      </MDTypography>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleDownloadOrdersReport(
                            "cash on delivery",
                            "cash-on-delivery-orders-report"
                          )
                        }
                        disabled={isGeneratingReport}
                        sx={{
                          textTransform: "none",
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                          "&:hover": {
                            borderColor: "#b71c1c",
                            color: "#b71c1c",
                          },
                          "&.Mui-disabled": {
                            color: "#d32f2f",
                            borderColor: "#d32f2f",
                            opacity: 0.6,
                          },
                        }}
                      >
                        Download PDF Report
                      </Button>
                    </MDBox>
                    <MDBox pb={2}>
                      {isCashOnDeliveryOrdersLoading ? (
                        <MDBox py={5} display="flex" justifyContent="center">
                          <AppLoader size={36} />
                        </MDBox>
                      ) : (
                        <DataTable
                          table={cashOnDeliveryOrdersTable}
                          isSorted
                          canSearch={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === "cancelled" && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox
                      px={2}
                      py={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", md: "center" }}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1.5}
                    >
                      <MDTypography variant="h6" color="text">
                        Cancelled Orders
                      </MDTypography>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleDownloadOrdersReport("cancelled", "cancelled-orders-report")
                        }
                        disabled={isGeneratingReport}
                        sx={{
                          textTransform: "none",
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                          "&:hover": {
                            borderColor: "#b71c1c",
                            color: "#b71c1c",
                          },
                          "&.Mui-disabled": {
                            color: "#d32f2f",
                            borderColor: "#d32f2f",
                            opacity: 0.6,
                          },
                        }}
                      >
                        Download PDF Report
                      </Button>
                    </MDBox>
                    <MDBox pb={2}>
                      {isCancelledOrdersLoading ? (
                        <MDBox py={5} display="flex" justifyContent="center">
                          <AppLoader size={36} />
                        </MDBox>
                      ) : (
                        <DataTable
                          table={cancelledOrdersTable}
                          isSorted
                          canSearch={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === "overview" && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    {renderGaugeCard("Refresh Tokens", refreshTokenCount, "key", "warning")}
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    {renderGaugeCard("Error Log Files", errorLogFileCount, "error", "error")}
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    {renderGaugeCard(
                      "Database Size",
                      databaseSizeMb,
                      "storage",
                      "success",
                      dbSizeGaugeOptions
                    )}
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>{renderUserRoleSummaryCard()}</MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
          {isProcessLoading && (
            <MDBox
              sx={{
                position: "fixed",
                inset: 0,
                zIndex: 2000,
                bgcolor: "rgba(255,255,255,0.45)",
                backdropFilter: "blur(1px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AppLoader size={42} />
            </MDBox>
          )}
          <Footer />
          <MDSnackbar
            color="info"
            icon="notifications"
            title={notificationSnackbar.title}
            content={notificationSnackbar.content}
            dateTime="just now"
            open={notificationSnackbar.open}
            onClose={() =>
              setNotificationSnackbar((prev) => ({
                ...prev,
                open: false,
              }))
            }
            close={() =>
              setNotificationSnackbar((prev) => ({
                ...prev,
                open: false,
              }))
            }
          />
        </>
      )}

      <Dialog open={isOrderDetailOpen} onClose={handleCloseOrderDetails} fullWidth maxWidth="md">
        <DialogTitle>Order Details: {selectedOrder ? getOrderId(selectedOrder) : "-"}</DialogTitle>
        <DialogContent dividers>
          <MDBox mb={2}>
            <MDTypography variant="h6" color="text">
              Customer Information
            </MDTypography>
            <MDBox mt={1}>
              <MDTypography variant="button" color="text" display="block">
                Name: {selectedOrder ? getCustomer(selectedOrder) : "-"}
              </MDTypography>
              <MDTypography variant="button" color="text" display="block">
                Email: {selectedOrder ? getCustomerEmail(selectedOrder) : "-"}
              </MDTypography>
              <MDTypography variant="button" color="text" display="block">
                Phone: {selectedOrder ? getCustomerPhone(selectedOrder) : "-"}
              </MDTypography>
              <MDTypography variant="button" color="text" display="block">
                Billing Address: {selectedOrder ? getBillingAddress(selectedOrder) : "-"}
              </MDTypography>
              <MDBox mt={1.5}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => openInvoicePdf(selectedOrder)}
                  disabled={!selectedOrder || !getInvoiceUrl(selectedOrder)}
                  sx={{
                    textTransform: "none",
                    color: "#ffffff !important",
                    "&.Mui-disabled": {
                      color: "#ffffff !important",
                      opacity: 0.65,
                    },
                  }}
                >
                  View Invoice PDF
                </Button>
              </MDBox>
            </MDBox>
          </MDBox>
          <MDBox mt={1}>
            <MDTypography variant="h6" color="text">
              Order Status Flow
            </MDTypography>
            <MDBox mt={1.5} display="flex" alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
              {(selectedOrder ? getOrderStatusHistory(selectedOrder) : []).map(
                (statusStep, index, arr) => (
                  <MDBox key={`status-step-${index}`} display="flex" alignItems="center">
                    <MDBox
                      px={1.5}
                      py={0.5}
                      borderRadius="lg"
                      sx={{
                        bgcolor:
                          statusStep === "Cancelled"
                            ? "grey.500"
                            : statusStep === "Cash on Delivery"
                            ? "warning.main"
                            : statusStep === "Payment Completed" || statusStep === "Delivered"
                            ? "success.main"
                            : "error.main",
                        color: "#fff",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        lineHeight: 1.2,
                      }}
                    >
                      {statusStep}
                    </MDBox>
                    {index < arr.length - 1 && (
                      <MDBox mx={1.2} sx={{ color: "text.secondary", fontWeight: 700 }}>
                        ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¾ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢
                      </MDBox>
                    )}
                  </MDBox>
                )
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDetails} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Dashboard;
